export const CONSENT_STORAGE_KEY = "proforma_privacy_consent";
export const CONSENT_VERSION = 2;

export const PRIVACY_POLICY_URL =
  process.env.REACT_APP_PRIVACY_POLICY_URL || "https://www.optony.energy/privacy-policy";

export const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  consentGiven: false,
  analytics: true,
  doNotSell: false,
  gpcDetected: false,
  timestamp: null,
};

export function isGpcEnabled() {
  return typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
}

export function shouldEnableAnalytics({ consentGiven, analytics, gpcDetected }) {
  if (gpcDetected) {
    return !!(consentGiven && analytics);
  }
  if (!consentGiven) {
    return true;
  }
  return !!analytics;
}

export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;

    return parsed;
  } catch {
    return null;
  }
}

export function isAnalyticsConsentEnabled() {
  return getAnalyticsEnabledFromStorage();
}

export function saveConsent(consent) {
  const payload = {
    ...consent,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
  return payload;
}

function clearPostHogPersistence() {
  if (typeof window === "undefined") return;

  Object.keys(localStorage).forEach((key) => {
    // Keep PostHog opt-out state; clearing it would re-enable capturing.
    if (key.startsWith("__ph_opt_in_out_")) return;

    if (key.startsWith("ph_") || key.includes("posthog")) {
      localStorage.removeItem(key);
    }
  });

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0]?.trim();
    if (name && (name.startsWith("ph_") || name.includes("posthog"))) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    }
  });
}

export function getAnalyticsEnabledFromStorage() {
  const stored = getStoredConsent();
  const gpcDetected = isGpcEnabled();

  return shouldEnableAnalytics({
    consentGiven: !!stored?.consentGiven,
    analytics: stored?.consentGiven ? !!stored?.analytics : true,
    gpcDetected,
  });
}

function getEnabledPostHogConfig() {
  return {
    persistence: "localStorage+cookie",
    autocapture: true,
    capture_pageview: true,
    disable_session_recording: false,
    advanced_disable_decide: false,
    disable_persistence: false,
    opt_out_persistence_by_default: false,
  };
}

function getDisabledPostHogConfig() {
  return {
    persistence: "memory",
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: true,
    advanced_disable_decide: true,
    disable_persistence: true,
    opt_out_persistence_by_default: true,
  };
}

export function applyAnalyticsConsent(posthog, enabled) {
  if (!posthog?.__loaded) return;

  if (enabled) {
    posthog.set_config(getEnabledPostHogConfig());
    posthog.opt_in_capturing({ capture_event_name: null });
    return;
  }

  posthog.opt_out_capturing();
  if (typeof posthog.stopSessionRecording === "function") {
    posthog.stopSessionRecording();
  }
  posthog.set_config(getDisabledPostHogConfig());
  clearPostHogPersistence();
  // Re-apply after clearing persistence so opt-out state is not lost.
  posthog.opt_out_capturing();
}

export function getInitialPostHogOptions() {
  const analyticsEnabled = isAnalyticsConsentEnabled();

  return {
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
    opt_out_capturing_by_default: !analyticsEnabled,
    opt_out_persistence_by_default: !analyticsEnabled,
    respect_dnt: true,
    ...(analyticsEnabled ? getEnabledPostHogConfig() : getDisabledPostHogConfig()),
    loaded: (client) => {
      applyAnalyticsConsent(client, analyticsEnabled);
    },
  };
}

export function resolveInitialConsent() {
  const gpcDetected = isGpcEnabled();
  const stored = getStoredConsent();

  if (stored?.consentGiven) {
    return {
      ...stored,
      gpcDetected,
      bannerVisible: false,
    };
  }

  if (gpcDetected) {
    const gpcConsent = saveConsent({
      consentGiven: true,
      analytics: false,
      doNotSell: true,
      gpcDetected: true,
    });

    return {
      ...gpcConsent,
      bannerVisible: true,
    };
  }

  return {
    ...DEFAULT_CONSENT,
    gpcDetected: false,
    bannerVisible: true,
  };
}
